/**
 * Ledger state shared by the list, the sidebar and the item pane.
 *
 * Module-level, like library.ts: closing or re-assigning an item in the
 * detail pane has to show in its list row and in the sidebar's counts
 * straight away. After an edit the item is put back where it was, or, when
 * the view no longer lists it (done, dropped, archived, reopened, re-kinded),
 * taken out at once, so the people's counts drop with it (the pane keeps it,
 * to reopen; reopened, the view is read again to put it back). The list is
 * only refetched when the view changes, the pane asks or a to-do is added.
 */

import type {
  ItemPatch,
  LedgerCounts,
  LedgerItem,
  LedgerList,
  LedgerView
} from '@satisa-memory/shared'
import { ref } from 'vue'
import { api, unwrap } from './api'
import { leavesView } from './ledger'

const list = ref<LedgerList | null>(null)
const counts = ref<LedgerCounts | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
/** The item being saved, so its row and pane can show it. */
const busy = ref<string | null>(null)

let generation = 0
/** The view `list` holds, and the words it was searched for. */
let shown: LedgerView | null = null
let searched = ''

async function load(view: LedgerView, q = '') {
  const mine = ++generation
  shown = view
  searched = q.trim()
  loading.value = true
  error.value = null
  try {
    const [l, c] = await Promise.all([
      unwrap(api.api.items.$get({ query: { view, ...(searched ? { q: searched } : {}) } })),
      unwrap(api.api.items.counts.$get())
    ])
    if (mine !== generation) return
    list.value = l
    counts.value = c
  } catch (e) {
    if (mine === generation) error.value = e instanceof Error ? e.message : 'Failed to load'
  } finally {
    if (mine === generation) loading.value = false
  }
}

async function refreshCounts() {
  try {
    counts.value = await unwrap(api.api.items.counts.$get())
  } catch {
    // The counts are a convenience; the list is what matters.
  }
}

/** The item from the list when it is there, else from the Worker (a deep link). */
async function getItem(id: string): Promise<LedgerItem> {
  const known = list.value?.items.find((i) => i.id === id)
  if (known) return known
  return unwrap(api.api.items[':id'].$get({ param: { id } }))
}

/** Save the owner's change, and put the new version where the old one was. */
async function patch(item: LedgerItem, body: ItemPatch): Promise<LedgerItem> {
  busy.value = item.id
  error.value = null
  try {
    const next = await unwrap(api.api.items[':id'].$patch({ param: { id: item.id }, json: body }))
    const held = list.value
    if (held && shown && !held.items.some((i) => i.id === next.id)) {
      // Back from being archived out of the view: read it again to place it.
      void load(shown, searched)
    } else {
      if (held) {
        list.value = {
          ...held,
          items:
            shown && leavesView(shown, next)
              ? held.items.filter((i) => i.id !== next.id)
              : held.items.map((i) => (i.id === next.id ? next : i))
        }
      }
      void refreshCounts()
    }
    return next
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Could not save'
    throw e
  } finally {
    busy.value = null
  }
}

/**
 * A to-do from the quick add: the view on screen is read again, so the new
 * item lands in its group and order as the Worker has it (when it belongs in
 * the view at all), and the counts follow.
 */
function added() {
  if (shown) void load(shown, searched)
  else void refreshCounts()
}

export function useLedger() {
  return { list, counts, loading, error, busy, load, getItem, patch, added }
}
